import { IconType } from 'react-icons';
import {
  LuLayoutDashboard,
  LuPackage,
  LuTruck,
  LuUsers,
  LuMap,
  LuFileText,
  LuChartLine,
  LuSettings,
  LuBuilding2,
  LuCar,
  LuUserCheck,
  LuMapPin,
  LuDollarSign,
  LuBell,
  LuShield,
  LuDatabase,
  LuPuzzle,
  LuFlag,
  LuBot,
} from 'react-icons/lu';

export interface NavigationItem {
  id: string;
  label: string;
  labelKey?: string; // i18n translation key
  href?: string;
  icon?: IconType;
  children?: NavigationItem[];
  badge?: {
    content: string | number;
    colorPalette?: string;
  };
  divider?: boolean;
  sectionLabel?: boolean; // Section header (e.g., "OPERATIONS", "FLEET")
  permission?: string;
  /**
   * Restrict this item to specific tenant/user country codes (e.g. ['US']).
   * When set, the item is hidden unless the current country is in the list.
   * Used to keep US-only modules (e.g. IFTA reporting) out of TR tenants (B-181).
   * Undefined → visible everywhere.
   */
  countries?: string[];
}

export const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    labelKey: 'navigation.dashboard',
    href: '/dashboard',
    icon: LuLayoutDashboard,
  },
  {
    id: 'orders',
    label: 'Orders',
    labelKey: 'navigation.orders',
    href: '/orders',
    icon: LuPackage,
    badge: {
      content: 'New',
      colorPalette: 'red',
    },
  },
  {
    id: 'shipments',
    label: 'Shipments',
    labelKey: 'navigation.shipments.title',
    href: '/shipments',
    icon: LuTruck,
    children: [
      {
        id: 'shipments-active',
        label: 'Active Shipments',
        labelKey: 'navigation.shipments.active',
        href: '/shipments/active',
      },
      {
        id: 'shipments-planning',
        label: 'Planning',
        labelKey: 'navigation.shipments.planning',
        href: '/shipments/planning',
      },
      {
        id: 'shipments-completed',
        label: 'Completed',
        labelKey: 'navigation.shipments.completed',
        href: '/shipments/completed',
      },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    labelKey: 'navigation.customers',
    href: '/customers',
    icon: LuUsers,
  },
  {
    id: 'fleet',
    label: 'Fleet',
    labelKey: 'navigation.fleet.title',
    icon: LuCar,
    children: [
      {
        id: 'drivers',
        label: 'Drivers',
        labelKey: 'navigation.fleet.drivers',
        href: '/fleet/drivers',
        icon: LuUserCheck,
      },
      {
        id: 'maintenance',
        label: 'Maintenance',
        labelKey: 'navigation.fleet.maintenance',
        href: '/fleet/maintenance',
      },
    ],
  },
  {
    id: 'routes',
    label: 'Routes',
    labelKey: 'navigation.routes',
    href: '/routes',
    icon: LuMap,
  },
  {
    id: 'tracking',
    label: 'Live Tracking',
    labelKey: 'navigation.tracking',
    href: '/tracking',
    icon: LuMapPin,
    badge: {
      content: 12,
      colorPalette: 'green',
    },
  },
  {
    id: 'agents',
    label: 'Agent Runner',
    labelKey: 'navigation.agents',
    href: '/agents',
    icon: LuBot,
  },
  {
    id: 'divider-1',
    label: '',
    divider: true,
  },
  {
    id: 'finance',
    label: 'Finance',
    labelKey: 'navigation.finance.title',
    href: '/finance',
    icon: LuDollarSign,
    children: [
      {
        id: 'invoices',
        label: 'Invoices',
        labelKey: 'navigation.finance.invoices',
        href: '/finance/invoices',
      },
      {
        id: 'payments',
        label: 'Payments',
        labelKey: 'navigation.finance.payments',
        href: '/finance/payments',
      },
      {
        id: 'expenses',
        label: 'Expenses',
        labelKey: 'navigation.finance.expenses',
        href: '/finance/expenses',
      },
      {
        id: 'contracts',
        label: 'Contracts',
        labelKey: 'navigation.finance.contracts',
        href: '/finance/contracts',
      },
      {
        id: 'claims',
        label: 'Claims',
        labelKey: 'navigation.finance.claims',
        href: '/finance/claims',
      },
      {
        id: 'capacity-reservations',
        label: 'Capacity Reservations',
        labelKey: 'navigation.finance.capacityReservations',
        href: '/finance/capacity-reservations',
      },
      {
        id: 'milestones',
        label: 'Milestones',
        labelKey: 'navigation.finance.milestones',
        href: '/finance/milestones',
        icon: LuFlag,
      },
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    labelKey: 'navigation.documents',
    href: '/documents',
    icon: LuFileText,
  },
  {
    id: 'reports',
    label: 'Reports',
    labelKey: 'navigation.reports.title',
    icon: LuChartLine,
    children: [
      {
        id: 'reports-overview',
        label: 'Overview',
        labelKey: 'navigation.reports.overview',
        href: '/reports',
        icon: LuChartLine,
      },
      {
        id: 'reports-designer',
        label: 'Report Designer',
        labelKey: 'navigation.reports.designer',
        href: '/reports/designer',
        icon: LuPuzzle,
      },
      {
        id: 'reports-schedules',
        label: 'Schedules',
        labelKey: 'navigation.reports.schedules',
        href: '/reports/schedules',
        icon: LuBell,
      },
    ],
  },
  {
    id: 'divider-2',
    label: '',
    divider: true,
  },
  {
    id: 'admin',
    label: 'Administration',
    labelKey: 'navigation.admin.title',
    icon: LuShield,
    permission: 'admin',
    children: [
      {
        id: 'tenants',
        label: 'Tenants',
        labelKey: 'navigation.admin.tenants',
        href: '/admin/tenants',
        icon: LuBuilding2,
      },
      {
        id: 'integrations',
        label: 'Integrations',
        labelKey: 'navigation.admin.integrations',
        href: '/admin/integrations',
        icon: LuPuzzle,
      },
      {
        id: 'system',
        label: 'System',
        labelKey: 'navigation.admin.system',
        href: '/admin/system',
        icon: LuDatabase,
      },
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    labelKey: 'navigation.notificationCenter',
    href: '/notifications',
    icon: LuBell,
    badge: {
      content: 3,
      colorPalette: 'blue',
    },
  },
  {
    id: 'settings',
    label: 'Settings',
    labelKey: 'navigation.settings',
    icon: LuSettings,
    children: [
      {
        id: 'profile',
        label: 'Profile',
        labelKey: 'navigation.settings.profile',
        href: '/settings/profile',
      },
      {
        id: 'tenant',
        label: 'Tenant',
        labelKey: 'navigation.settings.tenant',
        href: '/settings/tenant',
      },
      {
        id: 'notifications-settings',
        label: 'Notifications',
        labelKey: 'navigation.settings.notifications',
        href: '/settings/notifications',
      },
    ],
  },
];

// Helper function to find navigation item by path
export function findNavigationItemByPath(
  items: NavigationItem[],
  path: string
): NavigationItem | null {
  for (const item of items) {
    if (item.href === path) {
      return item;
    }
    if (item.children) {
      const found = findNavigationItemByPath(item.children, path);
      if (found) return found;
    }
  }
  return null;
}

// Helper function to get breadcrumb items from path
export function getBreadcrumbItems(path: string): NavigationItem[] {
  const segments = path.split('/').filter(Boolean);
  const breadcrumbs: NavigationItem[] = [];
  let currentPath = '';

  for (let i = 0; i < segments.length; i++) {
    currentPath += `/${segments[i]}`;
    const item = findNavigationItemByPath(navigationItems, currentPath);

    if (item) {
      breadcrumbs.push(item);
    } else {
      // Create a default breadcrumb for unmatched paths
      breadcrumbs.push({
        id: segments[i],
        label: segments[i].charAt(0).toUpperCase() + segments[i].slice(1),
        href: currentPath,
      });
    }
  }

  return breadcrumbs;
}

// Helper function to get localized navigation items
export function getLocalizedNavigationItems(
  items: NavigationItem[],
  t: (key: string) => string
): NavigationItem[] {
  return items.map((item) => {
    const localizedItem = { ...item };

    // Use translation if labelKey is provided
    if (item.labelKey) {
      localizedItem.label = t(item.labelKey);
    }

    // Recursively localize children
    if (item.children) {
      localizedItem.children = getLocalizedNavigationItems(item.children, t);
    }

    return localizedItem;
  });
}
